import { api } from './apiClient';

export type BookingService = {
  id: string;
  name: string;
  duration: number;
  price: number;
  description?: string;
  active?: boolean;
  /** Bloco 36: profissionais que oferecem o serviço (vazio/ausente = todos). */
  professionalIds?: string[];
};

export type BookingLocation = {
  id: string;
  name: string;
  type: 'online' | 'presential';
  address?: string;
  link?: string;
  active?: boolean;
  /** Bloco 36: profissionais que atendem neste local (vazio/ausente = todos). */
  professionalIds?: string[];
};

export type BookingProfessional = {
  id: string;
  name: string;
  formation: string;
  crp: string;
  photo: string;
  bio?: string;
  active?: boolean;
  serviceIds?: string[];
  /**
   * Bloco 29 (só na resposta pública): quando o profissional tem configuração
   * própria, o portal usa estes serviços e locais (e a API, os horários dele).
   */
  hasOwnConfig?: boolean;
  services?: BookingService[];
  locations?: BookingLocation[];
};

export type BookingTimeSlot = {
  id?: string;
  start: string;
  end: string;
  /** Bloco #67: PRESENCIAL_ONLINE = o paciente escolhe entre os dois locais. */
  modality?: 'PRESENCIAL' | 'ONLINE' | 'PRESENCIAL_ONLINE';
  locationId?: string;
  /** Bloco #67: teleconsulta da faixa PRESENCIAL_ONLINE. */
  onlineLocationId?: string;
  serviceIds?: string[];
};

export type BookingDayHours = { day: string; active: boolean; slots: BookingTimeSlot[] };

/** Snapshot publicado da página de agendamento. */
export type BookingConfig = {
  clinic?: {
    name?: string;
    welcomeMessage?: string;
    complementaryText?: string;
    links?: { label: string; url: string }[];
    social?: { instagram?: string; tiktok?: string; youtube?: string };
  };
  appearance?: {
    backgroundType?: 'blank' | 'gradient' | 'image';
    gradientFrom?: string;
    gradientTo?: string;
    imageUrl?: string;
    transparency?: number;
    fontFamily?: string;
    fontSize?: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    /** Bloco #68: "blank" (Vazio) mostra só o fundo na faixa lateral. Sem valor = texto personalizado. */
    sideContent?: 'blank' | 'custom-text';
    /** Bloco #69: cor do texto da faixa lateral (#RRGGBB). Sem valor = branco (padrão de sempre). */
    textColor?: string;
  };
  services?: BookingService[];
  locations?: BookingLocation[];
  professionals?: BookingProfessional[];
  hours?: BookingDayHours[];
  openDaysLimit?: number;
  slotIntervalMinutes?: number;
  minAdvanceHours?: number;
  autoConfirm?: boolean;
  /** Bloco #64: Modo Recesso / Férias. */
  recess?: RecessConfig;
};

export type RecessConfig = {
  enabled: boolean;
  /** YYYY-MM-DD */
  returnDate?: string | null;
  notice?: string | null;
  /** Mensagem de acolhimento no WhatsApp com {Nome}, {Nome da Clínica} e {Data de Retorno}. */
  whatsappTemplate?: string | null;
};

export const DEFAULT_RECESS_WHATSAPP_TEMPLATE =
  'Olá, {Nome}! Recebemos sua solicitação de atendimento na {Nome da Clínica}. Informamos que nossas atividades presenciais estão em recesso e retornaremos no dia {Data de Retorno}. Seu contato já está registrado em nossa lista de espera prioritária e entraremos em contato assim que as triagens forem retomadas. Em caso de emergência ou crise, procure o serviço de saúde mais próximo (como CAPS ou UPA) ou ligue 188 (CVV). Agradecemos a compreensão!';

export type PublishedBookingPage = {
  id: string;
  slug: string;
  active: boolean;
  config: BookingConfig;
  publishedAt: string;
};

export type PublicBookingPage = {
  slug: string;
  publishedAt: string;
  clinic: BookingConfig['clinic'] | null;
  appearance: BookingConfig['appearance'] | null;
  services: BookingService[];
  locations: BookingLocation[];
  professionals: BookingProfessional[];
  booking: {
    openDaysLimit: number;
    slotIntervalMinutes: number;
    minAdvanceHours: number;
    autoConfirm: boolean;
  };
  /** Bloco #64: quando ativo, o portal mostra a lista de espera no lugar da grade. */
  recess?: { enabled: true; returnDate: string | null; notice: string | null } | null;
};

export type WaitlistPeriod = 'MANHA' | 'TARDE' | 'NOITE';
export type WaitlistAgeGroup = 'CRIANCA' | 'ADOLESCENTE' | 'ADULTO' | 'IDOSO' | 'FAMILIAR' | 'CASAL';
export type WaitlistStatus = 'PENDENTE' | 'EM_CONTATO' | 'AGENDADO' | 'DESISTIU';

export type WaitlistEntry = {
  id: string;
  name: string;
  phone: string;
  reason: string;
  preferredPeriod: WaitlistPeriod | null;
  ageGroup: WaitlistAgeGroup;
  status: WaitlistStatus;
  notes: string | null;
  patientId: string | null;
  lgpdConsentAt: string;
  createdAt: string;
  updatedAt: string;
};

export const WAITLIST_PERIOD_LABELS: Record<WaitlistPeriod, string> = { MANHA: 'Manhã', TARDE: 'Tarde', NOITE: 'Noite' };
export const WAITLIST_AGE_LABELS: Record<WaitlistAgeGroup, string> = { CRIANCA: 'Criança', ADOLESCENTE: 'Adolescente', ADULTO: 'Adulto', IDOSO: 'Idoso', FAMILIAR: 'Familiar', CASAL: 'Casal' };
export const WAITLIST_STATUS_LABELS: Record<WaitlistStatus, string> = { PENDENTE: 'Pendente', EM_CONTATO: 'Em contato', AGENDADO: 'Agendado', DESISTIU: 'Desistiu' };

/** Inscrição pública na lista de espera (Modo Recesso). */
export function joinWaitlist(
  slug: string,
  payload: { name: string; phone: string; reason: string; preferredPeriod?: WaitlistPeriod; ageGroup: WaitlistAgeGroup; lgpdConsent: true },
): Promise<{ id: string; status: WaitlistStatus; whatsapp: string; returnDate: string | null }> {
  return api.post(`/public/booking/${encodeURIComponent(slug)}/waitlist`, payload, { skipAuth: true });
}

export function fetchWaitlist(): Promise<WaitlistEntry[]> {
  return api.get<WaitlistEntry[]>('/waitlist');
}

export function updateWaitlistEntry(id: string, payload: { status?: WaitlistStatus; notes?: string }): Promise<WaitlistEntry> {
  return api.patch<WaitlistEntry>(`/waitlist/${encodeURIComponent(id)}`, payload);
}

/** Bloco #71: tira o interessado da lista (não mexe em paciente/agendamentos criados a partir dele). */
export function deleteWaitlistEntry(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/waitlist/${encodeURIComponent(id)}`);
}

export function convertWaitlistEntryToPatient(id: string, assignedProfessionalId?: string): Promise<{ patientId: string; created: boolean; entry: WaitlistEntry }> {
  return api.post(`/waitlist/${encodeURIComponent(id)}/patient`, assignedProfessionalId ? { assignedProfessionalId } : {});
}

export type PublicSlot = {
  start: string;
  end: string;
  occupiedEnd?: string;
  modality?: string;
  locationId?: string;
  onlineLocationId?: string;
  serviceIds?: string[];
};

export type AppointmentResponse = {
  id: string;
  userId?: string;
  professionalId?: string;
  patientName: string;
  patientPhone: string;
  patientEmail?: string | null;
  date: string;
  start: string;
  end: string;
  serviceName?: string | null;
  modality: string;
  locationName?: string | null;
  notes?: string | null;
  status: 'PENDENTE' | 'CONFIRMADO' | 'CANCELADO';
  source: string;
  /** Paciente da base vinculado (encontrado ou criado na reserva). */
  patientId?: string | null;
  /** "Compareceu" / "Não Compareceu", registrado pelo profissional. */
  attendance?: string | null;
  /** Bloco #55: preço do serviço gravado na reserva. */
  serviceValue?: string | null;
  /** Bloco #54: resposta do paciente pelo link do WhatsApp. */
  patientResponse?: 'CONFIRMADO' | 'CANCELADO' | null;
  /** Bloco #56: sala do Google Meet criada para esta teleconsulta. */
  meetLink?: string | null;
};

export type UpdateAppointmentPayload = {
  date?: string;
  start?: string;
  end?: string;
  professionalId?: string;
  patientId?: string;
  serviceName?: string;
  modality?: string;
  locationName?: string;
  notes?: string;
  attendance?: string;
  status?: 'PENDENTE' | 'CONFIRMADO' | 'CANCELADO';
};

// --- Área autenticada --------------------------------------------------

export function publishBookingPage(payload: {
  slug: string;
  config: BookingConfig;
  active?: boolean;
}): Promise<PublishedBookingPage> {
  return api.put<PublishedBookingPage>('/booking-page', payload);
}

/** Serviços, locais e horários do usuário logado, guardados no servidor (bloco 29). */
export type ProfessionalAgendaConfig = {
  services: BookingService[];
  locations: BookingLocation[];
  hours: BookingDayHours[];
  updatedAt?: string;
};

export function saveMyAgendaConfig(config: {
  services: unknown[];
  locations: unknown[];
  hours: unknown[];
}): Promise<ProfessionalAgendaConfig> {
  return api.put<ProfessionalAgendaConfig>('/booking-page/professional-config', config);
}

export function fetchMyAgendaConfig(): Promise<ProfessionalAgendaConfig | null> {
  return api.get<ProfessionalAgendaConfig | null>('/booking-page/professional-config');
}

/** Bloco 36: horários de um profissional da clínica, lidos/salvos por administrador ou recepção. */
export function fetchAgendaConfigFor(userId: string): Promise<ProfessionalAgendaConfig | null> {
  return api.get<ProfessionalAgendaConfig | null>(`/booking-page/professional-config/${encodeURIComponent(userId)}`);
}

export function saveAgendaConfigFor(
  userId: string,
  config: { services: unknown[]; locations: unknown[]; hours: unknown[] },
): Promise<ProfessionalAgendaConfig> {
  return api.put<ProfessionalAgendaConfig>(`/booking-page/professional-config/${encodeURIComponent(userId)}`, config);
}

export function fetchMyBookingPage(): Promise<PublishedBookingPage | null> {
  return api.get<PublishedBookingPage | null>('/booking-page');
}

export function fetchAppointments(from?: string, to?: string): Promise<AppointmentResponse[]> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  return api.get<AppointmentResponse[]>(`/appointments${query ? `?${query}` : ''}`);
}

/** Remarca, anota comparecimento ou cancela uma reserva do portal. */
export function updateAppointment(
  id: string,
  payload: UpdateAppointmentPayload,
): Promise<AppointmentResponse> {
  return api.patch<AppointmentResponse>(`/appointments/${encodeURIComponent(id)}`, payload);
}

/**
 * Cancelar libera o horário no link externo imediatamente. `notifyPatient`:
 * bloco #53 — o paciente recebe o aviso de cancelamento por WhatsApp.
 */
export function cancelAppointment(id: string, notifyPatient = false): Promise<AppointmentResponse> {
  return api.patch<AppointmentResponse>(`/appointments/${encodeURIComponent(id)}/status`, {
    status: 'CANCELADO',
    ...(notifyPatient ? { notifyPatient: true } : {}),
  });
}

// --- Portal público (sem login) ----------------------------------------

export function fetchPublicBookingPage(slug: string): Promise<PublicBookingPage> {
  return api.get<PublicBookingPage>(`/public/booking/${encodeURIComponent(slug)}`, {
    skipAuth: true,
  });
}

export function fetchPublicDates(
  slug: string,
  professionalId?: string,
  serviceId?: string,
  /** Bloco #67: só as faixas que aceitam o local escolhido. */
  locationId?: string,
): Promise<{ dates: string[] }> {
  const params = new URLSearchParams();
  if (professionalId) params.set('professionalId', professionalId);
  if (serviceId) params.set('serviceId', serviceId);
  if (locationId) params.set('locationId', locationId);
  const q = params.toString();
  return api.get(`/public/booking/${encodeURIComponent(slug)}/dates${q ? `?${q}` : ''}`, {
    skipAuth: true,
  });
}

export function fetchPublicTimes(
  slug: string,
  date: string,
  professionalId?: string,
  serviceId?: string,
  locationId?: string,
): Promise<{ date: string; slots: PublicSlot[] }> {
  const params = new URLSearchParams({ date });
  if (professionalId) params.set('professionalId', professionalId);
  if (serviceId) params.set('serviceId', serviceId);
  if (locationId) params.set('locationId', locationId);
  return api.get(`/public/booking/${encodeURIComponent(slug)}/times?${params}`, {
    skipAuth: true,
  });
}

export function createPublicAppointment(
  slug: string,
  payload: {
    patientName: string;
    patientPhone: string;
    patientEmail?: string;
    date: string;
    start: string;
    professionalId?: string;
    serviceId?: string;
    serviceName?: string;
    modality?: string;
    locationName?: string;
    notes?: string;
  },
): Promise<AppointmentResponse & { patientCreated?: boolean }> {
  return api.post(`/public/booking/${encodeURIComponent(slug)}/appointments`, payload, {
    skipAuth: true,
  });
}

/** Lê o slug da URL (`?agendar=`) para abrir o portal do paciente. */
export function readBookingSlugFromUrl(search = window.location.search): string | null {
  const slug = new URLSearchParams(search).get('agendar');
  return slug?.trim() || null;
}
